/**
 * @fileoverview Settings → Profile: who the instructor is, and whether
 * organizations may find them.
 *
 * The first section on the page, because it is the one thing on it that
 * describes the person rather than the account. The plan badge sits beside
 * the name (it used to float in the page header, unexplained), the Founding
 * Ensemble medal sits beside that for members, and the identity facts are one
 * line rather than a stack of icon rows.
 *
 * "Open to teaching work" lives here as a second row rather than as a card of
 * its own: it is a fact about the profile — whether it is listed — and the
 * disclosure of what an organization can see folds behind a summary, since
 * the three lines matter at the moment of switching it on and are noise on
 * every other visit. The switch is consent, so the disclosure stays complete:
 * it enumerates exactly what the directory carries and what it never does.
 *
 * Organization membership and program affiliation are deliberately NOT here.
 * They are the Programs page's business — it is where memberships are seen,
 * joined and left.
 */

import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import FoundingMedal from "@/components/FoundingMedal";
import TierBadge from "@/components/settings/TierBadge";
import { SettingsRow, SettingsSection, Toggle } from "@/components/settings/SettingsSection";
import { Avatar, Button, Icon, Tag } from "@/components/studio/primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CountryChoice from "@/components/CountryChoice";
import TagInput from "@/components/TagInput";
import { COUNTRY_OPTIONS, countryForRegion } from "@/data/locations";
import api, { getErrorMessage } from "@/lib/api";
import { MARKETPLACE_ENABLED } from "@/lib/featureFlags";
import { isFoundingMember } from "@/lib/founding";
import { INSTRUMENTS } from "@/lib/instruments";
import { formatPhoneInput } from "@/lib/phone";
import TimezoneSelect from "@/components/TimezoneSelect";
import { avatarInitial, displayName } from "@/lib/userName";

const FOCUS_SUGGESTIONS = [
  "Classical", "Jazz", "Pop", "Rock", "Blues", "Folk", "Country",
  "R&B", "Hip-Hop", "Electronic", "World Music", "Musical Theater",
  "Improvisation", "Sight-Reading", "Ear Training", "Music Theory",
  "Songwriting", "Performance", "Film Scoring",
];

const FIELD_CLS =
  "w-full rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2.5 font-sans text-sm text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] transition-colors focus:border-[var(--accent-yellow)]/50 focus:outline-hidden";
const LABEL_CLS = "mb-1 block font-sans text-xs text-[var(--text-secondary)]";

/** The region list's "Not set" choice. Radix Select takes no empty value. */
const NO_REGION = "none";

/** A tag list with the text still in its box added, trimmed and only once. */
const withTyped = (list, typed) => [...new Set([...list, (typed || "").trim()].filter(Boolean))];

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={LABEL_CLS}>{label}</label>
      {children}
    </div>
  );
}

/** The inline edit form — the same fields onboarding collected. */
function ProfileForm({ user, onSaved, onCancel }) {
  // Fall back to splitting user.name for sessions that haven't been re-fetched
  // since the first/last migration.
  const fallbackParts = (user?.name || "").trim().split(/\s+/);
  const [form, setForm] = useState({
    first_name: user?.first_name || fallbackParts[0] || "",
    last_name: user?.last_name || fallbackParts.slice(1).join(" "),
    instruments_taught: user?.instruments_taught || [],
    city: user?.city || "",
    // Not sent: it only picks the region list. The region goes in `state`.
    // Opening on the country the stored region came from shows an
    // Australian or typed region rather than dropping it.
    country: countryForRegion(user?.state),
    state: (user?.state || "").trim(),
    years_experience: user?.years_experience ?? "",
    phone: user?.phone || "",
    focus_areas: user?.focus_areas || [],
    timezone: user?.timezone || "",
  });
  const [saving, setSaving] = useState(false);
  const [yearsError, setYearsError] = useState(null);
  // Text typed into a tag box without pressing Enter is still an answer, as in
  // onboarding: Save keeps it.
  const [instrumentInput, setInstrumentInput] = useState("");
  const [focusInput, setFocusInput] = useState("");
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const country = COUNTRY_OPTIONS.find((c) => c.id === form.country) || COUNTRY_OPTIONS[0];
  // A region belongs to its country's list, so a new country clears it.
  // Pressing the country already chosen keeps it.
  const chooseCountry = (id) => setForm((prev) => (id === prev.country ? prev : { ...prev, country: id, state: "" }));

  const save = async () => {
    // A number of years, if given, must be one the server takes (0 to 80),
    // checked here as onboarding does rather than left to a refusal.
    const years = String(form.years_experience ?? "").trim();
    if (years && !(/^\d+$/.test(years) && Number(years) <= 80)) {
      setYearsError("Enter a whole number of years, up to 80");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      const trimmedFirst = (form.first_name || "").trim();
      const trimmedLast = (form.last_name || "").trim();
      if (trimmedFirst !== (user?.first_name || "")) payload.first_name = trimmedFirst;
      if (trimmedLast !== (user?.last_name || "")) payload.last_name = trimmedLast;
      payload.instruments_taught = withTyped(form.instruments_taught, instrumentInput);
      payload.city = form.city || null;
      payload.state = form.state.trim() || null;
      payload.years_experience = years ? parseInt(years, 10) : null;
      payload.phone = form.phone || null;
      payload.focus_areas = withTyped(form.focus_areas, focusInput);
      // Only a change is a choice. The server records one made here as the
      // instructor's word on the matter, which nothing detected or remembered
      // from a schedule may overwrite (decision D3).
      if (form.timezone && form.timezone !== (user?.timezone || "")) payload.timezone = form.timezone;

      const res = await api.put("/auth/profile", payload);
      onSaved(res.data);
      toast.success("Profile updated!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update profile"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="profile-form">
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <input type="text" value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} className={FIELD_CLS} />
        </Field>
        <Field label="Last name">
          <input type="text" value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} className={FIELD_CLS} />
        </Field>
      </div>
      <Field label="Instruments taught">
        <TagInput
          value={form.instruments_taught}
          onChange={(v) => set({ instruments_taught: v })}
          onInputChange={setInstrumentInput}
          suggestions={INSTRUMENTS}
          placeholder="e.g. Piano, Guitar..."
        />
      </Field>
      <div>
        <span id="profile-country-label" className={LABEL_CLS}>Country</span>
        <CountryChoice value={country.id} onChange={chooseCountry} labelledBy="profile-country-label" testId="profile-country" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="City" htmlFor="profile-city">
          <input
            id="profile-city"
            type="text"
            value={form.city}
            onChange={(e) => set({ city: e.target.value })}
            maxLength={200}
            className={FIELD_CLS}
          />
        </Field>
        <Field label={country.regionLabel} htmlFor="profile-region">
          {country.regions ? (
            <Select value={form.state || NO_REGION} onValueChange={(v) => set({ state: v === NO_REGION ? "" : v })}>
              <SelectTrigger
                id="profile-region"
                data-testid="profile-region"
                className="h-[42px] rounded-xl border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] text-[var(--text-primary)]"
              >
                <SelectValue placeholder="Not set" />
              </SelectTrigger>
              <SelectContent className="max-h-60 border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
                <SelectItem value={NO_REGION} className="text-[var(--text-tertiary)]">Not set</SelectItem>
                {country.regions.map((st) => (
                  <SelectItem key={st} value={st}>{st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <input
              id="profile-region"
              data-testid="profile-region"
              type="text"
              value={form.state}
              onChange={(e) => set({ state: e.target.value })}
              placeholder="e.g. Ontario, Canada"
              maxLength={100}
              className={FIELD_CLS}
            />
          )}
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Years of experience" htmlFor="profile-years">
          <input
            id="profile-years"
            type="number"
            min="0"
            max="80"
            value={form.years_experience}
            onChange={(e) => {
              set({ years_experience: e.target.value });
              setYearsError(null);
            }}
            aria-invalid={yearsError ? true : undefined}
            aria-describedby={yearsError ? "profile-years-error" : undefined}
            className={FIELD_CLS}
          />
          {yearsError && (
            <p id="profile-years-error" role="alert" className="mt-1 font-sans text-xs text-[var(--state-danger)]">
              {yearsError}
            </p>
          )}
        </Field>
        <Field label="Timezone">
          <TimezoneSelect
            value={form.timezone}
            onChange={(e) => set({ timezone: e.target.value })}
            className={FIELD_CLS}
            aria-label="Timezone"
            data-testid="profile-timezone"
          />
        </Field>
        <Field label="Phone">
          {/* The (555) 555-5555 shape is a US number's; anywhere else the
              number is kept as typed, as in onboarding. */}
          <input
            type="tel"
            value={form.phone}
            onChange={(e) =>
              set({ phone: country.id === "US" ? formatPhoneInput(e.target.value) : e.target.value.slice(0, 20) })
            }
            placeholder={country.id === "US" ? "(555) 555-5555" : "Phone number"}
            className={FIELD_CLS}
            data-testid="profile-phone"
          />
        </Field>
      </div>
      <Field label="Focus areas">
        <TagInput
          value={form.focus_areas}
          onChange={(v) => set({ focus_areas: v })}
          onInputChange={setFocusInput}
          suggestions={FOCUS_SUGGESTIONS}
          placeholder="e.g. Classical, Jazz..."
        />
      </Field>
      <div className="flex gap-2 pt-1">
        <Button onPress={save} disabled={saving} className="px-5 py-2 text-[14px]" data-testid="profile-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save
        </Button>
        <Button onPress={onCancel} variant="bare" className="px-5 py-2 text-[14px]">
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** The identity header: avatar, name, badges, facts, chips. */
function ProfileHeader({ user, tier, onEdit }) {
  const name = displayName(user);
  const founding = isFoundingMember(user);
  const location = [user?.city, user?.state].filter(Boolean).join(", ");
  const facts = [
    user?.email,
    location || null,
    user?.timezone ? `Times in ${user.timezone.replaceAll("_", " ")}` : null,
    user?.years_experience != null ? `${user.years_experience} year${user.years_experience === 1 ? "" : "s"} teaching` : null,
    user?.phone || null,
  ].filter(Boolean);

  return (
    <div className="flex items-start gap-4 md:gap-5">
      {user?.picture ? (
        <img src={user.picture} alt={name} className="h-14 w-14 shrink-0 rounded-full md:h-16 md:w-16" />
      ) : (
        <Avatar name={avatarInitial(user)} size={60} accent />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="font-sans text-xl font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{name}</h2>
          <TierBadge tier={tier} />
          {founding && (
            <span
              className="inline-flex items-center gap-1.5 font-sans text-[12px] font-medium text-[var(--accent-yellow-text)]"
              data-testid="founding-member-mark"
            >
              <FoundingMedal size={18} /> Founding Ensemble
            </span>
          )}
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 font-sans text-[13px] text-[var(--text-tertiary)]">
          {facts.map((fact, i) => (
            <span key={fact} className="inline-flex items-center gap-x-2">
              {i > 0 && <span aria-hidden="true" className="text-[var(--text-quaternary)]">·</span>}
              <span>{fact}</span>
            </span>
          ))}
        </p>
        {(user?.instruments_taught?.length > 0 || user?.focus_areas?.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(user?.instruments_taught || []).map((inst) => (
              <Tag key={`i-${inst}`} accent className="px-2.5 py-1 text-[12px]">{inst}</Tag>
            ))}
            {(user?.focus_areas || []).map((area) => (
              <Tag key={`f-${area}`} className="px-2.5 py-1 text-[12px]">{area}</Tag>
            ))}
          </div>
        )}
      </div>
      <Button onPress={onEdit} variant="bare" icon="edit" className="px-4 py-2 text-[13px]" data-testid="profile-edit">
        Edit
      </Button>
    </div>
  );
}

/**
 * Discoverability by organizations. Opt-in, default off, and the copy
 * enumerates EXACTLY what an org can see — the directory carries experience
 * and tenure on top of the booking-profile fields, and a consent switch that
 * under-describes what it discloses isn't consent.
 */
function OpenToWorkRow({ user, setUser }) {
  // Seeded from the user payload and kept local so the switch responds
  // immediately; the server answer replaces it on save.
  const [openToWork, setOpenToWork] = useState(!!user?.open_to_work);
  const [saving, setSaving] = useState(false);

  // Optimistic like the notification toggles, and rolled back on failure —
  // a switch that silently didn't take is worse than one that flicks back.
  const toggle = async (next) => {
    setOpenToWork(next);
    setSaving(true);
    try {
      const res = await api.put("/auth/profile", { open_to_work: next });
      setUser?.(res.data);
      toast.success(next ? "Organizations can now find you" : "You're no longer listed");
    } catch (err) {
      setOpenToWork(!next);
      toast.error(getErrorMessage(err, "Couldn't update that"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsRow
      title="Open to teaching work"
      desc="Let music organizations on Hans find you and invite you to their programs. You decide on every invitation — nothing is shared until you accept."
      trailing={<Toggle on={openToWork} onChange={toggle} disabled={saving} label="Open to teaching work" testId="open-to-work-toggle" />}
      last
    >
      <details className="group mt-3">
        <summary className="cursor-pointer list-none font-sans text-[12px] font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] [&::-webkit-details-marker]:hidden">
          <span className="inline-flex items-center gap-1">
            <Icon name="chevronRight" size={13} className="transition-transform group-open:rotate-90" />
            What organizations can see
          </span>
        </summary>
        <div className="mt-2 rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-base)] p-3">
          <ul className="space-y-1 font-sans text-[12px] text-[var(--text-secondary)]">
            <li>· Your name, city and the instruments you teach</li>
            <li>· Your booking-page photo, bio and lesson formats</li>
            <li>· Your years of experience and how long you&apos;ve been on Hans</li>
          </ul>
          <p className="mt-2 font-sans text-[12px] text-[var(--text-tertiary)]">
            Never your email address, your students, or anything about your lessons. Organizations invite you
            through Hans, so they never need your contact details — and turning this off removes you from the
            directory immediately.
          </p>
        </div>
      </details>
    </SettingsRow>
  );
}

/**
 * @param {object} props
 * @param {object} props.user The instructor from /auth/me.
 * @param {function} props.setUser AuthContext setter, for saves.
 * @param {string} props.tier The tier to badge — the EFFECTIVE one.
 */
export default function ProfileSection({ user, setUser, tier }) {
  const [editing, setEditing] = useState(false);

  return (
    <SettingsSection title="Profile" testId="profile-section">
      <SettingsRow last={!MARKETPLACE_ENABLED}>
        {editing ? (
          <ProfileForm
            user={user}
            onSaved={(next) => {
              setUser?.(next);
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <ProfileHeader user={user} tier={tier} onEdit={() => setEditing(true)} />
        )}
      </SettingsRow>
      {MARKETPLACE_ENABLED && <OpenToWorkRow user={user} setUser={setUser} />}
    </SettingsSection>
  );
}
